/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { withRouter } from "react-router-dom";

import { endpoints } from "../../../shared/constants/endpoints";
import { orderMethods } from "../../../shared/constants/order";
import * as orderRepo from "../../../shared/repos/graphql/order";
import * as productRepo from "../../../shared/repos/graphql/product";
import { routeCountryPath } from "../../../shared/utilities/common";
import amplitudeAnalytics from "../../../shared/utilities/amplitude";

import Loader from "../../../components/Loader/Loader";
import styles from "./BoxSleeveSelection.module.css";
import brandRefreshStyles from "./BoxSleeveSelectionBrandRefresh.module.css";
import {
  getBackIcon,
  getGiftBoxPartProps,
  getGiftBoxRegionProps,
  getRadioIcons,
  isBoxSleeveBrandRefresh,
} from "./boxSleeveSelectionBrandRefreshClasses";

const backIcon = require("../imgs/back-icon.svg");
const lightBackIcon = require("../imgs/light-back-icon.svg");
const brandRefreshBackIcon = require("./imgs/brand-refresh-back-icon.svg");
const brandRefreshRadioOff = require("./imgs/brand-refresh-radio-off.svg");
const brandRefreshRadioOn = require("./imgs/brand-refresh-radio-on.svg");

/** Amplitude "Select Gift Option" — matches PDP spec for continuing without a sleeve */
const GIFT_OPTION_NO_BOX = "No Gift Box";

const SleeveOption = ({ option, isSelected, onSelect, disabled, reason }) => {
  const handleSelect = () => {
    if (disabled) return;
    onSelect(option.id);
  };
  const isBrandRefresh = isBoxSleeveBrandRefresh();
  const brandRadioIcons = getRadioIcons(
    brandRefreshRadioOff,
    brandRefreshRadioOn
  );

  return (
    <div
      className={`flex flex-col items-center p-0 transition text-center ${
        isBrandRefresh
          ? "bg-transparent"
          : `gap-2 rounded-lg bg-white dark:bg-[#2A2A2A] ${
              isSelected ? "bg-[#F9F8FA] dark:bg-[#333333]" : ""
            }`
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      onClick={handleSelect}
      onKeyDown={e => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(option.id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      {...getGiftBoxPartProps("sleeve-option")}
    >
      <div
        className={
          isBrandRefresh
            ? "relative w-full aspect-square shrink-0 overflow-hidden rounded-[6px]"
            : "w-full aspect-square rounded-lg overflow-hidden bg-[#F5F5F5]"
        }
        {...getGiftBoxPartProps("sleeve-option-image")}
      >
        <img
          src={
            option.productImage || option.image || "/default-product-image.jpg"
          }
          alt={option.productTitle || option.title}
          className={
            isBrandRefresh
              ? "absolute inset-0 w-full h-full object-cover rounded-[6px] pointer-events-none"
              : "w-full h-full object-cover"
          }
          {...getGiftBoxPartProps("sleeve-option-photo")}
        />
      </div>
      <div className="w-full" {...getGiftBoxPartProps("sleeve-option-copy")}>
        <h3
          className={
            isBrandRefresh
              ? "m-0 w-full text-center"
              : "font-medium text-[16px] leading-5 text-[#2D2D2D] dark:text-white m-0"
          }
          {...getGiftBoxPartProps("sleeve-option-title")}
        >
          {option.productTitle || option.title}
        </h3>
        {disabled && reason ? (
          <p
            className="text-[13px] leading-[18px] text-[#666666] dark:text-[#CCCCCC] m-0 mt-1"
            {...getGiftBoxPartProps("sleeve-option-reason")}
          >
            {reason}
          </p>
        ) : null}
      </div>
      <div
        className={isBrandRefresh ? "relative" : "relative mt-2"}
        {...getGiftBoxPartProps("sleeve-radio-wrap")}
      >
        <input
          type="radio"
          name="sleeve-selection"
          value={option.id}
          checked={isSelected}
          onChange={handleSelect}
          className={styles.radioInput}
          disabled={disabled}
        />
        {brandRadioIcons ? (
          <img
            src={isSelected ? brandRadioIcons.on : brandRadioIcons.off}
            alt=""
            width={24}
            height={24}
            className="block w-6 h-6"
            {...getGiftBoxPartProps("sleeve-radio")}
          />
        ) : (
          <span
            className={styles.radioCustom}
            {...getGiftBoxPartProps("sleeve-radio")}
          />
        )}
      </div>
    </div>
  );
};

SleeveOption.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    productImage: PropTypes.string,
    image: PropTypes.string,
    productTitle: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  reason: PropTypes.string
};

SleeveOption.defaultProps = {
  disabled: false,
  reason: null
};

const BoxSleeveSelection = ({ match, history }) => {
  const [sleeveData, setSleeveData] = useState(null);
  const [selectedSleeveId, setSelectedSleeveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [productImage, setProductImage] = useState(null);
  const [productTitle, setProductTitle] = useState("Gift Box Options");
  const viewGiftOptionsFired = useRef(false);

  const currency = useSelector(state => state.currency.currency);
  const userCartId = useSelector(state => state.user.userCartId);
  const userOrderMethod = useSelector(state => state.user.userOrderMethod);
  const order = useSelector(state => state.order);

  const loadSleeveData = useCallback(async () => {
    const { params } = match;
    const { id: productId, orderProductId: orderProductIdFromParams } = params;

    // Prefer orderProductId from URL, then from location state, then fallback to most recent cart item
    // TODO: In a cleanup follow-up, remove the fallback to location state and cart search once all entry points pass orderProductId in the URL.
    let orderProductId =
      orderProductIdFromParams || history.location.state?.orderProductId;

    // If not in URL or location state, try to find the most recent cart item for this product
    if (!orderProductId) {
      const cartItem = order.items
        ?.filter(item => item.product.id === parseInt(productId, 10))
        ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      if (cartItem) {
        orderProductId = cartItem.id;
      }
    }

    if (!orderProductId) {
      setError(
        "Unable to determine product configuration. Please try adding the product again."
      );
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch sleeve options and base product in parallel
      const [sleeveResp, productResp] = await Promise.all([
        productRepo.getProductBoxSleeve(
          parseInt(orderProductId, 10),
          parseInt(productId, 10)
        ),
        productRepo.getProduct(parseInt(productId, 10))
      ]);

      // Base product data for default image
      const baseProduct = productResp?.data?.product;
      if (baseProduct) {
        setProductImage(baseProduct.productImage || null);
        setProductTitle(
          baseProduct.metaHeading || baseProduct.title || "Gift Box Options"
        );
      }

      const sleeveResponseData = sleeveResp.data.productBoxSleeve;

      const fireViewGiftOptionsOnce = () => {
        if (!baseProduct || viewGiftOptionsFired.current) {
          return;
        }
        viewGiftOptionsFired.current = true;
        amplitudeAnalytics.trackViewGiftOptions({
          productName: baseProduct.metaHeading || baseProduct.title || "",
          productId: baseProduct.id,
        });
      };

      if (!sleeveResponseData) {
        // No results returned at all — treat as empty results so the UI can offer a primary
        // "Continue without a gift box" action instead of an error screen.
        setSleeveData({
          availableSleeveOptions: [],
          unavailableSleeveOptions: []
        });
        fireViewGiftOptionsOnce();
        setLoading(false);
        return;
      }

      // Even if there are no available options, we will show unavailable options (disabled)
      setSleeveData({
        ...sleeveResponseData,
        availableSleeveOptions: sleeveResponseData.availableSleeveOptions || [],
        unavailableSleeveOptions:
          sleeveResponseData.unavailableSleeveOptions || []
      });

      fireViewGiftOptionsOnce();
      setLoading(false);
    } catch (err) {
      setError("Failed to load sleeve options");
      setLoading(false);
    }
  }, [match, history.location.state, order.items]);

  useEffect(() => {
    loadSleeveData();
  }, [loadSleeveData]);

  const handleSleeveSelect = useCallback(optionId => {
    setSelectedSleeveId(optionId);
  }, []);

  const handleContinueWithNoGift = useCallback(() => {
    const { id: productId } = match.params;
    if (productId) {
      amplitudeAnalytics.trackSelectGiftOption({
        productName: productTitle,
        productId,
        optionName: GIFT_OPTION_NO_BOX,
      });
    }
    // Continue with regular box (no sleeve) - go to appropriate page
    const addLink =
      userOrderMethod === orderMethods.shipping
        ? endpoints.shippingPage
        : endpoints.menu;
    history.push(routeCountryPath(addLink));
  }, [history, userOrderMethod, match.params, productTitle]);

  const handleContinue = useCallback(async () => {
    if (!userCartId || !sleeveData) {
      return;
    }

    const { params } = match;
    const { id: productId, orderProductId: orderProductIdFromParams } = params;

    setUpdating(true);
    setError(null);

    try {
      // Get orderProductId from URL param first, then location state, then most recent cart item
      let orderProductId =
        orderProductIdFromParams || history.location.state?.orderProductId;

      if (!orderProductId) {
        const cartItem = order.items
          ?.filter(item => item.product.id === parseInt(productId, 10))
          ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

        if (!cartItem) {
          setError("Cart item not found. Please try adding the product again.");
          setUpdating(false);
          return;
        }
        orderProductId = cartItem.id;
      }

      // Add sleeve option if selected
      if (selectedSleeveId) {
        await orderRepo.addOptionsToOrderProduct(
          userCartId,
          parseInt(productId, 10),
          parseInt(orderProductId, 10),
          [
            {
              optionId: parseInt(selectedSleeveId, 10),
              remove: false
            }
          ]
        );
        const available = sleeveData.availableSleeveOptions || [];
        const unavailable = sleeveData.unavailableSleeveOptions || [];
        const chosen = [...available, ...unavailable].find(
          o => String(o.id) === String(selectedSleeveId)
        );
        const optionName = chosen?.productTitle || chosen?.title || "";
        if (optionName) {
          amplitudeAnalytics.trackSelectGiftOption({
            productName: productTitle,
            productId,
            optionName,
          });
        }
      }

      // Navigate to appropriate next page
      const addLink =
        userOrderMethod === orderMethods.shipping
          ? endpoints.shippingPage
          : endpoints.menu;
      history.push(routeCountryPath(addLink));
    } catch (err) {
      setError("Failed to update your selections. Please try again.");
    }

    setUpdating(false);
  }, [
    selectedSleeveId,
    userCartId,
    sleeveData,
    match,
    history.location.state,
    order.items,
    history,
    userOrderMethod,
    productTitle
  ]);

  if (loading) {
    return (
      <div className={styles.pageLoaderWrapper}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer} {...getGiftBoxRegionProps()}>
        <p
          className={styles.errorMessage}
          {...getGiftBoxPartProps("error-message")}
        >
          {error}
        </p>
      </div>
    );
  }

  if (!sleeveData) {
    return null;
  }

  const available = sleeveData.availableSleeveOptions || [];
  const unavailable = sleeveData.unavailableSleeveOptions || [];
  const noAvailableResults = available.length === 0;
  const isBrandRefresh = isBoxSleeveBrandRefresh();
  const resolvedBackIcon = getBackIcon(backIcon, brandRefreshBackIcon);
  const selectedPreviewOption = selectedSleeveId
    ? available.find(opt => opt.id === selectedSleeveId)
    : null;
  const previewImageSrc =
    (selectedPreviewOption && selectedPreviewOption.image) ||
    productImage ||
    (available[0] && available[0].image) ||
    "/default-product-image.jpg";
  const previewImageAlt =
    (selectedPreviewOption && selectedPreviewOption.title) ||
    productTitle ||
    "Gift Box Options";

  return (
    <div
      className={
        isBrandRefresh
          ? "min-h-screen"
          : "min-h-screen bg-white text-[#2D2D2D] dark:bg-[#1A1A1A] dark:text-white"
      }
      {...getGiftBoxRegionProps()}
    >
      <div
        className={
          isBrandRefresh
            ? undefined
            : "p-5 border-b border-[#E5E5E5] dark:border-[#333333]"
        }
        {...getGiftBoxPartProps("header")}
      >
        <button
          type="button"
          onClick={() => history.goBack()}
          className={
            isBrandRefresh
              ? "flex items-center bg-transparent border-0 cursor-pointer p-0"
              : "flex items-center gap-2 bg-transparent border-0 text-[#2D2D2D] dark:text-white font-bold text-[16px] cursor-pointer p-0 hover:opacity-80"
          }
          {...getGiftBoxPartProps("back-button")}
        >
          {isBrandRefresh ? (
            <span {...getGiftBoxPartProps("back-icon-button")}>
              <span {...getGiftBoxPartProps("back-icon")}>
                <img
                  src={resolvedBackIcon}
                  alt=""
                  width={24}
                  height={24}
                  className="block"
                />
              </span>
            </span>
          ) : (
            <>
              <img src={backIcon} alt="Back" className="block dark:hidden" />
              <img
                src={lightBackIcon}
                alt="Back"
                className="hidden dark:block"
              />
            </>
          )}
          <span {...getGiftBoxPartProps("back-label")}>Back</span>
        </button>
      </div>

      <div
        className={
          isBrandRefresh
            ? undefined
            : "flex gap-10 p-10 max-w-[1200px] mx-auto md:flex-row sm:gap-6 sm:p-5 flex-col"
        }
        {...getGiftBoxPartProps("main-content")}
      >
        {isBrandRefresh ? (
          <div {...getGiftBoxPartProps("product-photos")}>
            <div {...getGiftBoxPartProps("product-image-frame")}>
              <div {...getGiftBoxPartProps("product-image")}>
                <img
                  src={previewImageSrc}
                  alt={previewImageAlt}
                  width={290}
                  height={290}
                  {...getGiftBoxPartProps("product-image-photo")}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center flex-1 bg-[#F6F3F7] max-w-[478px] max-h-[478px]">
            <img
              src={previewImageSrc}
              alt={previewImageAlt}
              className="max-w-[250px] max-h-[250px] w-auto h-auto rounded-lg"
            />
          </div>
        )}
        <div
          className={isBrandRefresh ? undefined : "flex-1"}
          {...getGiftBoxPartProps("selection-area")}
        >
          {isBrandRefresh ? (
            <div {...getGiftBoxPartProps("section-title")}>
              <div {...getGiftBoxPartProps("section-title-top")}>
                <h2 {...getGiftBoxPartProps("page-title")}>Gift Box Options</h2>
                <p {...getGiftBoxPartProps("price")}>
                  (+
                  {currency.symbol}
1.00)
                </p>
              </div>
              <p {...getGiftBoxPartProps("page-subtitle")}>
                Choose up to 1 design
              </p>
            </div>
          ) : (
            <div className="mb-8 flex justify-between">
              <div>
                <h2 className="font-bold text-lg leading-6 text-[#2D2D2D] dark:text-white m-0">
                  Gift Box Options
                </h2>
                <p className="text-[16px] leading-5 text-[#666666] dark:text-[#CCCCCC] m-0">
                  Choose up to 1 design
                </p>
              </div>
              <p className="font-semibold text-[16px] leading-5 text-[#331942] m-0">
                (+
                {currency.symbol}
                1.00 )
              </p>
            </div>
          )}

          {!noAvailableResults && (
            <div
              className={
                isBrandRefresh
                  ? undefined
                  : "grid grid-cols-2 gap-4 mb-2 md:grid-cols-3"
              }
              {...getGiftBoxPartProps("sleeve-options")}
            >
              {available.map(option => (
                <SleeveOption
                  key={option.id}
                  option={option}
                  isSelected={selectedSleeveId === option.id}
                  onSelect={handleSleeveSelect}
                  disabled={false}
                />
              ))}
            </div>
          )}

          {noAvailableResults && unavailable.length > 0 && (
            <>
              <h3
                className={
                  isBrandRefresh
                    ? undefined
                    : "font-montserratBold text-[16px] leading-5 mt-4 mb-2"
                }
                {...getGiftBoxPartProps("unavailable-heading")}
              >
                Currently unavailable at this store
              </h3>
              <div
                className={
                  isBrandRefresh
                    ? undefined
                    : "grid grid-cols-2 gap-4 mb-8 md:grid-cols-3"
                }
                {...getGiftBoxPartProps("sleeve-options")}
              >
                {unavailable.map(option => (
                  <SleeveOption
                    key={option.id}
                    option={option}
                    isSelected={false}
                    onSelect={() => {}}
                    disabled
                    reason={option.reason}
                  />
                ))}
              </div>
            </>
          )}

          <div
            className={isBrandRefresh ? undefined : "flex flex-col gap-4 md:gap-3"}
            {...getGiftBoxPartProps("actions")}
          >
            {selectedSleeveId && (
              <button
                type="button"
                onClick={handleContinue}
                disabled={updating}
                className={
                  isBrandRefresh
                    ? `relative w-full transition-colors${
                        updating ? " opacity-50" : ""
                      }`
                    : `relative w-full py-4 px-6 rounded-full font-semibold text-[16px] transition bg-[#331942] border border-[#331942] text-white hover:bg-[#221128] hover:border-[#221128] ${
                        updating ? "opacity-50" : ""
                      }`
                }
                {...getGiftBoxPartProps("continue-button")}
              >
                {updating && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader height={24} />
                  </div>
                )}
                {updating ? "Adding..." : "Continue"}
              </button>
            )}

            {noAvailableResults ? (
              <button
                type="button"
                onClick={handleContinueWithNoGift}
                className={
                  isBrandRefresh
                    ? "relative w-full transition-colors"
                    : "relative w-full py-4 px-6 rounded-full font-semibold text-[16px] transition bg-[#331942] border border-[#331942] text-white hover:bg-[#221128] hover:border-[#221128]"
                }
                {...getGiftBoxPartProps("no-gift-button-primary")}
              >
                Continue without a gift box
              </button>
            ) : (
              <button
                type="button"
                onClick={handleContinueWithNoGift}
                className={
                  isBrandRefresh
                    ? `relative w-full transition-colors ${brandRefreshStyles.noGiftButtonHover}`
                    : "relative w-full py-4 px-6 rounded-full font-semibold text-[16px] transition bg-transparent border border-solid text-[#331942] border-[#331942] hover:bg-[#331942] hover:text-white hover:border-[#331942] dark:text-white dark:border-white dark:hover:bg-white dark:hover:text-[#1A1A1A] dark:hover:border-white"
                }
                {...getGiftBoxPartProps("no-gift-button")}
              >
                {isBrandRefresh
                  ? "Continue with no gift options"
                  : "Continue with no gift box"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

BoxSleeveSelection.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      orderProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }).isRequired
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      state: PropTypes.shape({
        orderProductId: PropTypes.number
      })
    })
  }).isRequired
};

BoxSleeveSelection.defaultProps = {};

export default withRouter(BoxSleeveSelection);
